import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { message, Tooltip, Input, List } from "antd";
import {
	DeleteOutlined,
	DollarOutlined,
	UserOutlined,
	/* UsergroupAddOutlined, */ HeartOutlined,
	StopOutlined,
	CheckCircleOutlined,
	DownOutlined,
	UpOutlined,
	MinusOutlined,
	CloseOutlined,
	CommentOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	SendOutlined,
} from "@ant-design/icons";
import type { Creator, CreatorGroup } from "@/shared/types/campaign/creator";
import { creatorListAPI, GroupApproval } from "@/api/creatorList.ts";
import { getPlatformNameFromUrl } from "@/shared/utils/platformLinks.ts";
import { CREATOR_CATEGORIES, CREATOR_TIERS } from "@/api/creator.ts";
import { DynamicModal } from "@/shared/components/Modal/DynamicModal.tsx";
import { Icon, type IconName } from "@/shared/components/Icon/Icon.tsx";
import { AppSelect } from "@/shared/components/Select/AppSelect.tsx";
import { AppTooltip } from "@/shared/components/Tooltip/AppTooltip.tsx";
import { formatNumberCommas } from "@/shared/utils/formatters.ts";
import {
	CREATOR_CARD_NO_DATA_TEXT,
	formatEngagementRate,
	isUnavailableCreatorCardMetric,
} from "@/shared/utils/socialProfile.ts";
import { ResponsiveAvatar } from "@/shared/components/Avatar/ResponsiveAvatar.tsx";
import {
	formatRateDisplayValue,
	formatRateMoneyDisplayValue,
	isRateInputValid,
	parseRateInputValue,
	sanitizeRateInputValue,
} from "@/shared/utils/rateInput.ts";
import { formatFinalRateSummaryValue, useFinalRateEditing } from "../../hooks/useFinalRateEditing.ts";
import { StyledSelect } from "@/shared/components/Select/styledSelect.tsx";
import { getCreatorInterestStatusLabel, normalizeCreatorInterestStatus } from "../../utils/interestStatusHelper.ts";
import { CREATOR_INTEREST_STATUS_META, CreatorInterestStatus } from "@/shared/types/campaign/creatorInterestStatus.ts";
import { useAssignmentContactEmail } from "../../hooks/useAssignmentContactEmail.ts";
import { ApprovedGroupBatchModal } from "./ApprovedGroupBatchModal.tsx";
import { AppButton } from "@/shared/components/index.ts";
import { TAP_TARGET, TAP_TARGET_PRIMARY, mediaPhone, mediaTablet } from "@/shared/theme/breakpoints";
import { pressedFade, pressedTint } from "@/shared/theme/pressed";
import { POPUP_KEEP_IN_VIEWPORT } from "@/shared/utils/popupOverflow";
import {
	CREATOR_CARD_MAX_HEIGHT,
	CREATOR_CARD_MIN_WIDTH,
	PhoneFullLabel,
	PhoneShortLabel,
	creatorCardContainer,
	creatorCardPhoneControls,
	creatorGridContainer,
	mediaNarrowCard,
	mediaSingleColumn,
} from "../common/creatorCardPhone";

const GroupsContainer = styled.div`
	width: 100%;
	margin-top: 1rem;
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
`;

const GroupCard = styled.div<{ $isDragging?: boolean; $isDragOver?: boolean }>`
	background: white;
	border: 2px solid #ffe5f0;
	border-radius: 12px;
	padding: 1.5rem;
	cursor: ${(props) => (props.$isDragging ? "grabbing" : "default")};
	opacity: ${(props) => (props.$isDragging ? 0.5 : 1)};
	transition: all 0.2s;

	${mediaPhone`
		padding: 16px;
	`}
`;

const GroupHeader = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 1px solid #f0f0f0;

	${mediaPhone`
		gap: 12px;
		margin-bottom: 16px;
		padding-bottom: 12px;
	`}
`;

/**
 * The group name, with its "Client approved" badge beside it. The name keeps at least 240px; with a badge the
 * row sizes to name plus badge, so the badge never drops under a truncated name. When the actions don't fit
 * beside it they wrap onto their own row.
 */
const GroupTitleRow = styled.div<{ $hasBadge: boolean }>`
	display: flex;
	flex: 1 1 ${(props) => (props.$hasBadge ? "auto" : "240px")};
	flex-wrap: wrap;
	align-items: center;
	gap: 8px 12px;
	min-width: 0;

	${mediaPhone`
		flex-basis: 100%;
	`}
`;

const GroupTitle = styled.h2`
	/* A long name takes the row (and ellipsises); the badge then wraps below it. */
	flex: 0 1 auto;
	max-width: 100%;
	min-width: 0;
	font-size: 1.5rem;
	font-weight: 700;
	color: #333;
	margin: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

	${mediaPhone`
		font-size: 18px;
		line-height: 26px;
		white-space: normal;
		overflow-wrap: anywhere;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
	`}
`;

const GroupStats = styled.div`
	display: flex;
	align-items: center;
	gap: 1rem;
	flex-wrap: wrap;

	/* Phones: one stat per row so long rates never squeeze the labels. */
	${mediaPhone`
		flex-direction: column;
		align-items: stretch;
		gap: 6px;
	`}
`;

const StatItem = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem 1.5rem;
	background: #f5f5f5;
	border-radius: 8px;
	flex: 0 0 auto;

	${mediaPhone`
		padding: 8px 12px;
	`}
`;

const StatIcon = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	color: #666;
	font-size: 18px;
`;

const StatContent = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 0.5rem;

	/* Phones: the label on top and the figure under it, so a long rate never wraps beside a squeezed label. */
	${mediaPhone`
		flex-direction: column;
		align-items: flex-start;
		gap: 2px;
		min-width: 0;
	`}
`;

const StatLabel = styled.span`
	font-size: 0.875rem;
	color: #333;
	font-weight: 500;
`;

const StatValue = styled.span<{ $isRate?: boolean }>`
	font-size: 1rem;
	font-weight: 600;
	color: ${(props) => (props.$isRate ? "#52c41a" : "#333")};
`;

const GroupHeaderTop = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	/* Rows only: side by side the title and actions meet as they always have, so desktop is unchanged. */
	gap: 12px 0;
	width: 100%;

	${mediaPhone`
		gap: 8px;
	`}
`;

const GroupHeaderRight = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px 1.5rem;
	/*
	 * Beside the title (which grows to fill the row) the actions end up at the right edge. When they don't fit
	 * they wrap below it and read left to right from the title's edge, wrapping again if needed.
	 */
	flex: 0 1 auto;
	min-width: 0;

	/* Phones: See Approvals and Visible to Client, then Edit and Delete, then the full-width primary action. */
	${mediaPhone`
		gap: 0 16px;
	`}
`;

/** The primary group action with, on phones, its disabled reason as text (a touch screen can't show a title). */
const AddApprovedAction = styled.div`
	display: contents;

	${mediaPhone`
		display: flex;
		flex-direction: column;
		gap: 4px;
		flex: 1 1 100%;
		margin-top: 4px;

		> button {
			width: 100%;
			min-height: ${TAP_TARGET_PRIMARY}px;
		}
	`}
`;

const DisabledReason = styled.span`
	display: none;

	${mediaPhone`
		display: block;
		font-size: 12px;
		line-height: 18px;
		color: #707070;
	`}
`;

const ClientActionsContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 1.5rem;

	${mediaPhone`
		flex-wrap: wrap;
		gap: 8px 16px;
	`}
`;

const ClientActionText = styled.span`
	font-size: 14px;
	color: #4460ef;
	font-weight: 500;
	cursor: pointer;
	transition: opacity 0.2s;
	display: flex;
	align-items: center;
	gap: 0.5rem;

	&:hover {
		opacity: 0.8;
		text-decoration: underline;
	}
`;

const ClientSelect = styled(AppSelect)`
	min-width: 160px;
`;

const ClientApprovedBadge = styled.span`
	display: inline-flex;
	align-items: center;
	padding: 6px 12px 6px 10px;
	border-radius: 999px;
	background: #dbfabc;
	color: #1b7a46;
	font-family: "Poppins", sans-serif;
	font-size: 13px;
	font-weight: 500;
	line-height: normal;
	white-space: pre;
`;

const CommentInputLabel = styled.label`
	display: block;
	font-size: 14px;
	font-weight: 500;
	color: #333;
	margin-bottom: 8px;
`;

const CommentTextArea = styled(Input.TextArea)`
	border-radius: 8px;
	border: 1px solid #d9d9d9;
	padding: 12px;
	font-size: 14px;
	min-height: 120px;
	resize: none;

	&:focus {
		border-color: #4460ef;
		box-shadow: 0 0 0 2px rgba(68, 96, 239, 0.1);
	}
`;

const GroupActionButton = styled.button`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0;
	background: transparent;
	border: none;
	cursor: pointer;
	font-size: 14px;
	font-weight: 500;
	color: #333;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.8;
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.45;
	}

	${mediaPhone`
		min-height: ${TAP_TARGET}px;
		${pressedFade}
	`}
`;

const ApprovalIcon = styled(CheckCircleOutlined)`
	color: #4460ef;
	font-size: 18px;
`;

const EditIcon = styled(Icon)`
	width: 18px;
	height: 18px;
	cursor: pointer;
`;

const GroupToggleWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;

	${mediaPhone`
		min-height: ${TAP_TARGET}px;
	`}
`;

/* Phones: a transparent 44px hit area around a switch, leaving its look unchanged. */
const switchHitArea = mediaPhone`
	&::before {
		content: "";
		position: absolute;
		inset: -14px -6px;
	}
`;

const GroupToggleLabel = styled.span`
	font-size: 14px;
	color: #666;
	font-weight: 500;
`;

const CreatorToggleLabel = styled.span`
	color: #333;
	font-weight: 500;
	font-size: 12px;
`;

const GroupToggle = styled.div<{ $active: boolean }>`
	flex-shrink: 0;
	width: 32px;
	height: 18px;
	border-radius: 9px;
	background: ${(props) => (props.$active ? "#4460ef" : "#d9d9d9")};
	position: relative;
	cursor: pointer;
	transition: background 0.2s;

	&::after {
		content: "";
		position: absolute;
		top: 2px;
		left: ${(props) => (props.$active ? "16px" : "2px")};
		width: 14px;
		height: 14px;
		border-radius: 50%;
		background: white;
		transition: left 0.2s;
	}

	${switchHitArea}
`;

const SmallGroupToggle = styled.div<{ $active: boolean }>`
	flex-shrink: 0;
	width: 30px;
	height: 16px;
	border-radius: 9px;
	background: ${(props) => (props.$active ? "#4460ef" : "#d9d9d9")};
	position: relative;
	cursor: pointer;
	transition: background 0.2s;

	&::after {
		content: "";
		position: absolute;
		top: 2px;
		left: ${(props) => (props.$active ? "16px" : "2px")};
		width: 12px;
		height: 12px;
		border-radius: 50%;
		background: white;
		transition: left 0.2s;
	}

	${switchHitArea}
`;

const ExpandIcon = styled.div`
	width: 24px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	color: #f72586;
	transition: color 0.2s;

	&:hover {
		opacity: 0.8;
	}
`;

const CreatorsGrid = styled.div`
	${creatorGridContainer}
	display: grid;
	/* min(): a column never asks for more than the container, so narrow widths get one column. */
	grid-template-columns: repeat(auto-fill, minmax(min(${CREATOR_CARD_MIN_WIDTH}px, 100%), 1fr));
	gap: 1.5rem;

	${mediaPhone`
		gap: 16px;
	`}
	justify-content: center;
	width: 100%;
`;

const CreatorCard = styled.div<{ $isVisible: boolean }>`
	${creatorCardContainer}
	background: white;
	border-radius: 12px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	overflow: hidden;
	display: flex;
	flex-direction: column;
	transition: background 0.2s;
	width: 100%;
	aspect-ratio: 370 / 500;

	/* One column (any width): the card sizes to its content up to a cap; its body scrolls past that. */
	${mediaSingleColumn`
		aspect-ratio: auto;
		max-height: ${CREATOR_CARD_MAX_HEIGHT}px;
	`}

	${mediaPhone`
		aspect-ratio: auto;
		max-height: ${CREATOR_CARD_MAX_HEIGHT}px;
		${creatorCardPhoneControls}

		.ant-select {
			width: 100% !important;
		}
	`}
`;

const CardHeader = styled.div<{ $isVisible: boolean }>`
	padding: 1rem;
	border-bottom: 1px solid #f0f0f0;
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
	position: relative;
`;

const ProfileImage = styled(ResponsiveAvatar)`
	width: 48px;
	height: 48px;
	border-radius: 50%;
	object-fit: cover;
	flex-shrink: 0;
`;

const CreatorInfo = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	flex: 1;
	min-width: 0;
	justify-content: flex-start;
`;

const NameRow = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
`;

const CreatorName = styled.h3`
	margin: 0;
	font-size: 16px;
	font-weight: 600;
	color: #333;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;

	/* Keeps a long name clear of the delete icon in the card's top-right corner. */
	${mediaTablet`
		min-width: 0;
		padding-right: 32px;
	`}
`;

const CreatorHeaderBottom = styled.div<{ $compact?: boolean }>`
	display: flex;
	flex-direction: column;
	gap: ${(props) => (props.$compact ? "0.5rem" : "0.75rem")};
	margin-top: ${(props) => (props.$compact ? "0.25rem" : "0.5rem")};
`;

const ChannelsSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	align-items: center;
	padding-right: 60px;

	/*
	 * Phones: the section sits in the column beside the avatar, so it is pulled back under the avatar
	 * (48px + the header's 0.75rem gap) to centre on the card rather than on that column.
	 */
	${mediaPhone`
		padding-right: 0;
		margin-left: calc(-48px - 0.75rem);
	`}
`;

const ChannelsLabel = styled.span`
	font-size: 14px;
	color: #666;
	font-weight: 500;
`;

const ChannelsIcons = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
`;

const ChannelIcon = styled.a<{ $platform: string }>`
	width: 40px;
	height: 40px;
	border-radius: 6px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: opacity 0.2s;
	text-decoration: none;
	flex-shrink: 0;

	&:hover {
		opacity: 0.8;
	}

	${(props) => {
		switch (props.$platform.toLowerCase()) {
			case "tiktok":
				return `
          background: #000000;
        `;
			case "instagram":
				return `
          background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
        `;
			case "facebook":
				return `
          background: #1877f2;
        `;
			case "youtube":
				return `
          background: #ff0000;
        `;
			default:
				return `
          background: #d9d9d9;
        `;
		}
	}}
`;

const ChannelIconImage = styled(Icon)`
	width: 24px;
	height: 24px;
	object-fit: contain;
`;

const DeleteIcon = styled.div`
	width: 20px;
	height: 20px;
	cursor: pointer;
	position: absolute;
	top: 1rem;
	right: 1rem;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #f72586;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.8;
	}

	${mediaPhone`
		top: 4px;
		right: 4px;
		width: ${TAP_TARGET}px;
		height: ${TAP_TARGET}px;
		${pressedFade}
	`}
`;

const InterestAndRateContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin: 0.75rem 1rem;
	flex-wrap: nowrap;

	${mediaPhone`
		flex-wrap: wrap;
		gap: 8px 12px;
	`}
`;

// Creator interest / rate UI

const InterestButton = styled.button<{ $status?: CreatorInterestStatus }>`
	display: flex;
	align-items: center;
	gap: 0.375rem;
	padding: 0.375rem 0.75rem;
	border: 1px solid ${(props) => (props.$status ? CREATOR_INTEREST_STATUS_META[props.$status].border : "#d9d9d9")};
	border-radius: 16px;
	font-size: 12px;
	font-weight: 500;
	cursor: pointer;
	background: ${(props) => (props.$status ? CREATOR_INTEREST_STATUS_META[props.$status].bg : "#f5f5f5")};
	color: ${(props) => (props.$status ? CREATOR_INTEREST_STATUS_META[props.$status].color : "#666")};
	transition: all 0.2s;
	white-space: nowrap;
	flex-shrink: 0;

	&:hover {
		opacity: 0.8;
	}
`;

const HeartIcon = styled(HeartOutlined)`
	color: inherit;
	font-size: 14px;
`;

const NotInterestedIcon = styled(StopOutlined)`
	color: inherit;
	font-size: 12px;
`;

const RateInfo = styled.div<{ $visible?: boolean }>`
	display: flex;
	align-items: center;
	gap: 0.375rem;
	font-size: 12px;
	color: ${(props) => (props.$visible === false ? "#999" : "#333")};
	flex-shrink: 1;
	min-width: 0;

	.anticon {
		color: inherit;
	}

	/* Phones: a long rate drops under its label instead of running past the card. */
	${mediaPhone`
		flex-wrap: wrap;
		row-gap: 2px;
	`}
`;

const RateIcon = styled(DollarOutlined)`
	font-size: 14px;
	flex-shrink: 0;
`;

const RateLabel = styled.span`
	white-space: nowrap;
`;

const RateValue = styled.span<{ $visible?: boolean }>`
	color: ${(props) => (props.$visible === false ? "inherit" : "#52c41a")};
	font-weight: 600;
	white-space: nowrap;

	${mediaPhone`
		white-space: normal;
		overflow-wrap: anywhere;
	`}
`;

const TabsContainer = styled.div<{ $isVisible: boolean }>`
	display: flex;
	border-bottom: 1px solid #f0f0f0;
	padding: 0 1rem;
	gap: 0.5rem;
	overflow-x: auto;
`;

const Tab = styled.button<{ $active: boolean; $isVisible: boolean }>`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.75rem 0.5rem;
	border: none;
	background: transparent;
	cursor: pointer;
	font-size: 12px;
	color: ${(props) => (props.$active ? "#4460ef" : "#999")};
	border-bottom: ${(props) => (props.$active ? "2px solid #4460ef" : "2px solid transparent")};
	white-space: nowrap;
	opacity: 1;

	${mediaPhone`
		min-height: ${TAP_TARGET}px;
		${pressedTint}
	`}
`;

const TabIcon = styled(Icon)<{ $active: boolean }>`
	width: 16px;
	height: 16px;
	opacity: ${(props) => (props.$active ? 1 : 0.6)};
`;

const CardContent = styled.div<{ $isVisible: boolean }>`
	flex: 1;
	overflow-y: auto;
	padding: 1rem;
	opacity: 1;
`;

const ContentRow = styled.div`
	display: grid;
	grid-template-columns: 160px auto 1fr;
	gap: 12px;

	${mediaPhone`
		gap: 8px;
	`}
	margin-bottom: 8px;
	align-items: start;

	&:last-child {
		margin-bottom: 0;
	}

	/* Narrow cards: the label sits above its value, without the colon. */
	${mediaNarrowCard`
		grid-template-columns: minmax(0, 1fr);
		gap: 2px;
		margin-bottom: 12px;
	`}
`;

const ContentLabel = styled.span`
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	font-weight: 400;
	line-height: 24px;
	color: #707070;

	${mediaNarrowCard`
		font-size: 12px;
		line-height: 18px;
	`}
`;

const ContentSeparator = styled.span`
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	font-weight: 400;
	line-height: 24px;
	color: #707070;

	${mediaNarrowCard`
		display: none;
	`}
`;

const ContentValue = styled.span`
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	font-weight: 400;
	line-height: 24px;
	color: #303030;
	overflow-wrap: anywhere;
	min-width: 0;
	width: 100%;
	min-height: 28px;
`;

const RateInputShell = styled.div<{ $invalid?: boolean }>`
	position: relative;
	width: 100%;
	display: flex;
	align-items: center;
	border: 1px solid ${(props) => (props.$invalid ? "#ff4d4f" : "#cdd4f6")};
	border-radius: 8px;
	background: #fff;
	transition:
		border-color 0.2s,
		box-shadow 0.2s;

	&:focus-within,
	&:hover {
		border-color: ${(props) => (props.$invalid ? "#ff4d4f" : "#8193f1")};
	}
`;

const RateSuffix = styled.span`
	position: absolute;
	right: 10px;
	font-size: 12px;
	color: #666;
	pointer-events: none;
`;

const RateTextInput = styled.input`
	width: 100%;
	height: 24px;
	border: none;
	outline: none;
	font-size: 14px;
	color: #303030;
	padding: 0 42px 0 10px;
	border-radius: 8px;

	${mediaPhone`
		height: ${TAP_TARGET - 2}px;
		font-size: 16px;
	`}
`;

const ContactEmailField = styled.input<{ $invalid?: boolean }>`
	box-sizing: border-box;
	width: 100%;
	height: 24px;
	padding: 0 10px;
	border: 1px solid ${(props) => (props.$invalid ? "#d30024" : "#cdd4f6")};
	border-radius: 8px;
	background: #fff;
	color: #303030;
	font-size: 14px;
	transition: border-color 0.2s;
	&:focus,
	&:hover {
		outline: none;
		border-color: ${(props) => (props.$invalid ? "#d30024" : "#8193f1")};
	}

	${mediaPhone`
		height: ${TAP_TARGET}px;
		font-size: 16px;
	`}
`;
const ContactEmailError = styled.span`
	display: block;
	color: #d30024;
	font-size: 11px;
	line-height: 16px;
`;

const CardFooter = styled.div<{ $isVisible: boolean }>`
	padding: 1rem;
	border-top: 1px solid #f0f0f0;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 1rem;
	min-height: 48px;
`;

const AddToCampaignButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	padding: 0.5rem 1rem;
	border: none;
	background: transparent;
	color: #4460ef;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	border-radius: 6px;
	transition: background 0.2s;
	min-height: 28px;
	line-height: 1.5;

	&:hover {
		background: #f0f5ff;
	}

	${mediaPhone`
		min-height: ${TAP_TARGET}px;
		${pressedTint}
	`}
`;

const AddedToCampaignContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1.5rem;
	min-height: 28px;
	line-height: 1.5;
	width: 100%;
`;

const AddedToCampaignText = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	color: #52c41a;
	font-size: 14px;
	font-weight: 500;
`;

const AddedCheckIcon = styled(CheckCircleOutlined)`
	color: #52c41a;
	font-size: 16px;
`;

const RemoveButton = styled.button`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0;
	border: none;
	background: transparent;
	color: #4460ef;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.8;
	}

	${mediaPhone`
		min-height: ${TAP_TARGET}px;
		${pressedFade}
	`}
`;

const RemoveIcon = styled(MinusOutlined)`
	color: #4460ef;
	font-size: 16px;
`;

interface CreatorGroupGridProps {
	groups: CreatorGroup[];
	isCustomizing: boolean;
	creatorVisibility: { [key: string]: boolean };
	onCreatorVisibilityToggle: (creatorId: string) => void;
	onGroupReorder?: (reorderedGroups: CreatorGroup[]) => void;
	onCommentClick?: (creatorId: string) => void;
	onSettingsClick?: (creatorId: string) => void;
	onDeleteCreator?: (groupId: string, creatorId: string) => void;
	onSeeApprovals?: (groupId: string) => void;
	onGroupVisibilityToggle?: (groupId: string) => void;
	onEditGroup?: (groupId: string) => void;
	onAddToCampaign?: (creatorId: string) => void;
	onRemoveFromCampaign?: (assignmentId: string, groupId?: string) => void;
	onDeleteGroup?: (groupId: string) => Promise<void> | void;
	isClientOnly?: boolean;
	canManage?: boolean;
	campaignId?: string;
	blockId?: string;
	finalRateCurrency?: string;
	finalRateConvertedCurrency?: string;
	onCategoryTierChange?: (creatorId: string, field: "categories" | "tier", value: string[] | string) => void;
	isSaving?: boolean;
	onBeginCustomizeForRateEdit?: () => void;
	onFinalRateDraftChange?: (update: {
		creatorId: string;
		assignmentId?: string;
		finalRate: number | null;
		finalConvertedRate: number | null;
	}) => void;
	contentExecutionBlockId?: string;
	onContactEmailChange?: (assignmentId: string, contactEmail: string | null) => void;
	onBatchAdded?: (assignmentIds: string[]) => void;
	campaignName?: string;
}

type TabType = "basic" | "tiktok" | "instagram" | "facebook" | "youtube";

const PLATFORM_TABS: Record<Exclude<TabType, "basic">, string> = {
	tiktok: "TikTok",
	instagram: "Instagram",
	facebook: "Facebook",
	youtube: "YouTube",
};

const RESERVED_BASIC_FIELDS = new Set([
	"id",
	"name",
	"img_url",
	"tiktok",
	"instagram",
	"facebook",
	"youtube",
	"interested",
	"inList",
	"campaignId",
	"blockId",
	"assignment",
	"assignmentId",
	"userId",
	"contactEmail",
	// Hide meta/status fields from Basic tab
	"addedToCampaign",
	"creatorInterested",
	"finalRate",
	"finalConvertedRate",
	"recommendation",
	"socialAccounts",
]);

const HIDDEN_RATE_PATTERNS = [
	"finalratenumber",
	"defaultratenumber",
	"finalratecurrency",
	"defaultratecurrency",
	"finalrateconvertedcurrency",
	"defaultrateconvertedcurrency",
	"finalrate",
	"defaultrate",
	"finalconvertedrate",
];

const isHiddenRateField = (key: string): boolean => {
	const normalized = key.toLowerCase().replace(/[\s_-]/g, "");
	return HIDDEN_RATE_PATTERNS.some((p) => normalized === p);
};

const HUMAN_LABEL_CACHE = new Map<string, string>();

const formatLabel = (key: string) => {
	if (HUMAN_LABEL_CACHE.has(key)) {
		return HUMAN_LABEL_CACHE.get(key)!;
	}
	const label = key
		.replace(/([A-Z])/g, " $1")
		.replace(/[_-]/g, " ")
		.replace(/\s+/g, " ")
		.trim()
		.replace(/\b\w/g, (char) => char.toUpperCase());
	HUMAN_LABEL_CACHE.set(key, label);
	return label;
};

const isRenderableValue = (value: any) => {
	if (value === null || value === undefined) {
		return false;
	}
	if (typeof value === "string" && value.trim() === "") {
		return false;
	}
	if (Array.isArray(value)) {
		return value.length > 0;
	}
	if (typeof value === "object") {
		return Object.keys(value).length > 0;
	}
	return true;
};

const formatPercentageValue = (value: number) => {
	if (value > 1) {
		return `${value.toFixed(1)}%`;
	}
	return `${(value * 100).toFixed(1)}%`;
};

const formatDisplayValue = (key: string, value: any) => {
	if (isUnavailableCreatorCardMetric(key, value)) {
		return CREATOR_CARD_NO_DATA_TEXT;
	}

	if (!isRenderableValue(value)) {
		return "N/A";
	}

	if (Array.isArray(value)) {
		if (value.length === 0) {
			return "N/A";
		}
		if (typeof value[0] === "object" && value[0] !== null) {
			return value
				.map((entry) => {
					if ("ageRange" in entry && "percentage" in entry) {
						return `${entry.ageRange}: ${formatPercentageValue(Number(entry.percentage))}`;
					}
					return JSON.stringify(entry);
				})
				.join(", ");
		}
		return value.join(", ");
	}

	if (typeof value === "number") {
		const lowerKey = key.toLowerCase();
		if (lowerKey === "avgengagementrate") {
			return formatEngagementRate(value);
		}
		if (lowerKey.includes("percentage") || lowerKey.includes("rate")) {
			return formatPercentageValue(value);
		}
		return formatNumberCommas(value);
	}

	// Handle numeric strings (e.g. "92656256") by formatting as numbers
	if (typeof value === "string") {
		// Usernames are identifiers, not metrics. Facebook profile IDs can be
		// entirely numeric and must be displayed exactly as received.
		if (key.toLowerCase().includes("username")) {
			return value;
		}

		const cleaned = value.replace(/,/g, "").trim();
		if (/^-?\d+(?:\.\d+)?$/.test(cleaned)) {
			const n = Number(cleaned);
			const lowerKey = key.toLowerCase();
			if (lowerKey === "avgengagementrate") {
				return formatEngagementRate(n);
			}
			if (lowerKey.includes("percentage") || lowerKey.includes("rate")) {
				return formatPercentageValue(n);
			}
			return formatNumberCommas(n);
		}
	}

	if (typeof value === "boolean") {
		return value ? "Yes" : "No";
	}

	return String(value);
};

const looksLikeUrl = (value: string) => /^https?:\/\//i.test(value);

const getCurrentApprovedGroupApproval = (approvals: GroupApproval[] | undefined) => {
	const latestByUser = new Map<string, GroupApproval>();
	(approvals || []).forEach((approval) => {
		const current = latestByUser.get(approval.userId);
		if (!current || new Date(approval.createdAt).getTime() > new Date(current.createdAt).getTime()) {
			latestByUser.set(approval.userId, approval);
		}
	});
	return (
		Array.from(latestByUser.values())
			.filter((approval) => approval.status?.toLowerCase() === "approved")
			.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] || null
	);
};

const formatApprovalDate = (timestamp: string) =>
	new Intl.DateTimeFormat("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	}).format(new Date(timestamp));

export const CreatorGroupGrid: React.FC<CreatorGroupGridProps> = ({
	groups,
	isCustomizing,
	creatorVisibility,
	onCreatorVisibilityToggle: _onCreatorVisibilityToggle,
	onGroupReorder,
	onCommentClick: _onCommentClick,
	onSettingsClick: _onSettingsClick,
	onDeleteCreator,
	onSeeApprovals: _onSeeApprovals,
	onGroupVisibilityToggle,
	onEditGroup,
	onAddToCampaign,
	onRemoveFromCampaign,
	onDeleteGroup,
	isClientOnly = false,
	canManage = false,
	campaignId,
	blockId,
	finalRateCurrency = "USD",
	finalRateConvertedCurrency = "AUD",
	onCategoryTierChange,
	isSaving = false,
	contentExecutionBlockId,
	onContactEmailChange,
	onBatchAdded,
	campaignName,
}) => {
	const [expandedGroups, setExpandedGroups] = useState<{ [key: string]: boolean }>(
		groups.reduce((acc, group) => ({ ...acc, [group.id]: true }), {}),
	);
	const [groupVisibility, setGroupVisibility] = useState<{ [key: string]: boolean }>(
		groups.reduce((acc, group) => ({ ...acc, [group.id]: group.clientVisible ?? false }), {}),
	);
	const [activeTabs, setActiveTabs] = useState<{ [key: string]: TabType }>({});
	const [groupApprovals, setGroupApprovals] = useState<{ [groupId: string]: GroupApproval[] }>({});
	const [loadingApprovals, setLoadingApprovals] = useState<{ [groupId: string]: boolean }>({});
	const [tooltipOpen, setTooltipOpen] = useState<{ [groupId: string]: boolean }>({});
	const [userGroupApprovals, setUserGroupApprovals] = useState<{ [groupId: string]: GroupApproval | null }>({});
	const [loadingUserApprovals, setLoadingUserApprovals] = useState<{ [groupId: string]: boolean }>({});
	const [batchGroup, setBatchGroup] = useState<CreatorGroup | null>(null);
	const [batchSubmitting, setBatchSubmitting] = useState(false);
	const [visibilityChange, setVisibilityChange] = useState<{ group: CreatorGroup; visible: boolean } | null>(null);
	const [visibilitySubmitting, setVisibilitySubmitting] = useState(false);

	const [commentModalVisible, setCommentModalVisible] = useState<{ [groupId: string]: boolean }>({});
	const [commentTexts, setCommentTexts] = useState<{ [groupId: string]: string }>({});

	// Local visibility state to allow front-end-only toggling when no handler is provided.
	const [localCreatorVisibility, setLocalCreatorVisibility] = useState<{ [key: string]: boolean }>(creatorVisibility);

	useEffect(() => {
		setLocalCreatorVisibility(creatorVisibility);
	}, [creatorVisibility]);
	const [submittingComment, setSubmittingComment] = useState<{ [groupId: string]: boolean }>({});
	const allCreators = useMemo(() => groups.flatMap((group) => group.creators), [groups]);
	const contactEmail = useAssignmentContactEmail(campaignId, blockId, onContactEmailChange);
	const {
		rateDrafts,
		setRateDrafts,
		convertedRateDrafts,
		setConvertedRateDrafts,
		rateInvalid,
		setRateInvalid,
		convertedRateInvalid,
		setConvertedRateInvalid,
		debouncedSaveFinalRates,
	} = useFinalRateEditing({
		creators: allCreators,
		campaignId,
		blockId,
		creatorVisibility,
		finalRateCurrency,
		finalRateConvertedCurrency,
	});
	// Delete group modal state
	const [groupToDelete, setGroupToDelete] = useState<string | null>(null);
	const [deleting, setDeleting] = useState(false);

	const groupSums = useMemo(() => {
		const result: { [groupId: string]: { primary: number; converted: number } } = {};
		groups.forEach((group) => {
			result[group.id] = group.creators.reduce(
				(acc, creator) => {
					const p = parseRateInputValue(
						rateDrafts[creator.id] ??
							formatRateDisplayValue(
								creator.finalRate ?? creator.assignment?.finalRate,
								finalRateCurrency,
							) ??
							"",
						finalRateCurrency,
					);
					const c = parseRateInputValue(
						convertedRateDrafts[creator.id] ??
							formatRateDisplayValue(
								creator.finalConvertedRate ?? creator.assignment?.finalConvertedRate,
								finalRateConvertedCurrency,
							) ??
							"",
						finalRateConvertedCurrency,
					);
					return {
						primary: acc.primary + (typeof p === "number" ? p : 0),
						converted: acc.converted + (typeof c === "number" ? c : 0),
					};
				},
				{ primary: 0, converted: 0 },
			);
		});
		return result;
	}, [rateDrafts, convertedRateDrafts, groups, finalRateCurrency, finalRateConvertedCurrency]);

	const handleDeleteGroupConfirm = async () => {
		if (!groupToDelete) return;
		const targetGroupId = groupToDelete;
		setDeleting(true);
		try {
			if (onDeleteGroup) {
				await Promise.resolve(onDeleteGroup(targetGroupId));
			} else {
				message.error("Delete handler not provided");
			}
		} catch (error: any) {
			console.error("Error deleting group:", error);
			message.error(error?.response?.data?.message || error?.message || "Failed to delete group");
		} finally {
			setDeleting(false);
			setGroupToDelete(null);
		}
	};

	// Sync groupVisibility state when groups change (to reflect clientVisible from backend)
	useEffect(() => {
		setGroupVisibility(groups.reduce((acc, group) => ({ ...acc, [group.id]: group.clientVisible ?? false }), {}));
	}, [groups]);

	useEffect(() => {
		setActiveTabs((prev) => {
			const next = { ...prev };
			let changed = false;
			groups.forEach((group) => {
				group.creators.forEach((creator) => {
					if (!next[creator.id]) {
						next[creator.id] = "basic";
						changed = true;
					}
				});
			});
			return changed ? next : prev;
		});
	}, [groups]);

	useEffect(() => {
		setRateDrafts((prev) => {
			const next = { ...prev };
			let changed = false;
			groups.forEach((group) => {
				group.creators.forEach((creator) => {
					if (next[creator.id] === undefined) {
						next[creator.id] = formatRateDisplayValue(
							creator.finalRate ?? creator.assignment?.finalRate,
							finalRateCurrency,
						);
						changed = true;
					}
				});
			});
			return changed ? next : prev;
		});
		setConvertedRateDrafts((prev) => {
			const next = { ...prev };
			let changed = false;
			groups.forEach((group) => {
				group.creators.forEach((creator) => {
					if (next[creator.id] === undefined) {
						next[creator.id] = formatRateDisplayValue(
							creator.finalConvertedRate ?? creator.assignment?.finalConvertedRate,
							finalRateConvertedCurrency,
						);
						changed = true;
					}
				});
			});
			return changed ? next : prev;
		});
	}, [groups, finalRateCurrency, finalRateConvertedCurrency]);

	useEffect(() => {
		setActiveTabs((prev) => {
			const next = { ...prev };
			let changed = false;
			groups.forEach((group) => {
				group.creators.forEach((creator) => {
					if (!next[creator.id]) {
						next[creator.id] = "basic";
						changed = true;
					}
				});
			});
			return changed ? next : prev;
		});
	}, [groups]);

	useEffect(() => {
		setRateDrafts((prev) => {
			const next = { ...prev };
			let changed = false;
			groups.forEach((group) => {
				group.creators.forEach((creator) => {
					if (next[creator.id] === undefined) {
						next[creator.id] = formatRateDisplayValue(
							creator.finalRate ?? creator.assignment?.finalRate,
							finalRateCurrency,
						);
						changed = true;
					}
				});
			});
			return changed ? next : prev;
		});
		setConvertedRateDrafts((prev) => {
			const next = { ...prev };
			let changed = false;
			groups.forEach((group) => {
				group.creators.forEach((creator) => {
					if (next[creator.id] === undefined) {
						next[creator.id] = formatRateDisplayValue(
							creator.finalConvertedRate ?? creator.assignment?.finalConvertedRate,
							finalRateConvertedCurrency,
						);
						changed = true;
					}
				});
			});
			return changed ? next : prev;
		});
	}, [groups, finalRateCurrency, finalRateConvertedCurrency]);

	// Fetch user approvals for client users only
	useEffect(() => {
		if (!isClientOnly || !campaignId || !blockId) return;

		const fetchUserApprovals = async () => {
			for (const group of groups) {
				if (userGroupApprovals[group.id] !== undefined || loadingUserApprovals[group.id]) {
					continue;
				}

				setLoadingUserApprovals((prev) => ({ ...prev, [group.id]: true }));
				try {
					const approval = await creatorListAPI.getGroupApprovalByUser(campaignId, blockId, group.id);
					setUserGroupApprovals((prev) => ({ ...prev, [group.id]: approval }));
				} catch (error) {
					console.error(`Error fetching approval for group ${group.id}:`, error);
					setUserGroupApprovals((prev) => ({ ...prev, [group.id]: null }));
				} finally {
					setLoadingUserApprovals((prev) => {
						const newState = { ...prev };
						delete newState[group.id];
						return newState;
					});
				}
			}
		};

		fetchUserApprovals();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [groups, isClientOnly, campaignId, blockId]);

	const [draggedGroupIndex, setDraggedGroupIndex] = useState<number | null>(null);
	const [dragOverGroupIndex, setDragOverGroupIndex] = useState<number | null>(null);

	const toggleGroup = (groupId: string) => {
		setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
	};

	const handleTabChange = (creatorId: string, tab: TabType) => {
		setActiveTabs((prev) => ({ ...prev, [creatorId]: tab }));
	};

	const loadApprovals = async (groupId: string) => {
		if (!campaignId || !blockId) {
			return;
		}

		// If already loaded, don't reload
		if (groupApprovals[groupId] || loadingApprovals[groupId]) {
			return;
		}

		setLoadingApprovals((prev) => ({ ...prev, [groupId]: true }));

		try {
			const data = await creatorListAPI.getGroupApprovals(campaignId, blockId, groupId);
			setGroupApprovals((prev) => ({ ...prev, [groupId]: data }));
		} catch (error) {
			console.error("Error loading approvals:", error);
			setGroupApprovals((prev) => ({ ...prev, [groupId]: [] }));
		} finally {
			setLoadingApprovals((prev) => ({ ...prev, [groupId]: false }));
		}
	};

	useEffect(() => {
		if (isClientOnly || !canManage) return;
		groups.forEach((group) => void loadApprovals(group.id));
		// loadApprovals intentionally keys its own duplicate-request guard by group id.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [groups, isClientOnly, canManage, campaignId, blockId]);

	const handleBatchSubmit = async (assignmentIds: string[]) => {
		if (!batchGroup || !campaignId || !blockId || !contentExecutionBlockId) {
			message.error("Content Execution block not found for this market");
			return;
		}
		setBatchSubmitting(true);
		try {
			const result = await creatorListAPI.batchAddApprovedCreators(
				campaignId,
				blockId,
				batchGroup.id,
				contentExecutionBlockId,
				assignmentIds,
			);
			const successful = [...result.added, ...result.alreadyAdded];
			onBatchAdded?.(successful);
			setBatchGroup(null);
			message.success(
				`${result.added.length} creator${result.added.length === 1 ? "" : "s"} added to the campaign`,
			);
			if (result.failed.length)
				message.warning(
					`${result.failed.length} creator${result.failed.length === 1 ? "" : "s"} could not be added`,
				);
		} catch (error: any) {
			message.error(error?.response?.data?.error || "Failed to add approved creators");
		} finally {
			setBatchSubmitting(false);
		}
	};

	const confirmVisibilityChange = async () => {
		if (!visibilityChange || !campaignId || !blockId) return;
		setVisibilitySubmitting(true);
		try {
			await creatorListAPI.updateCreatorGroupClientVisibility(
				campaignId,
				blockId,
				visibilityChange.group.id,
				visibilityChange.visible,
			);
			setGroupVisibility((prev) => ({ ...prev, [visibilityChange.group.id]: visibilityChange.visible }));
			onGroupVisibilityToggle?.(visibilityChange.group.id);
			message.success(
				`Group ${visibilityChange.visible ? "made visible to" : "hidden from"} client successfully!`,
			);
			setVisibilityChange(null);
		} catch (error) {
			console.error("Error updating creator group client visibility:", error);
			message.error("Failed to update group visibility");
		} finally {
			setVisibilitySubmitting(false);
		}
	};

	const renderContentRows = (entries: Array<[string, any]>, creator?: Creator) => {
		if (entries.length === 0) {
			return (
				<ContentRow>
					<ContentLabel>Info</ContentLabel>
					<ContentSeparator>:</ContentSeparator>
					<ContentValue>No data available</ContentValue>
				</ContentRow>
			);
		}

		const rendered = new Set<string>();

		return entries.flatMap(([key, value]) => {
			if (isClientOnly) {
				if (!isRenderableValue(value)) return [];
				if (Array.isArray(value) && value.length === 0) return [];
				if (value === "" || value === "N/A") return [];
				const displayVal = formatDisplayValue(key, value);
				if (displayVal === "N/A") return [];
			}

			// Deduplicate aliased fields
			if (key === "category" || key === "categories") {
				if (rendered.has("category")) return [];
				rendered.add("category");
			}
			if (key === "creatorTier" || key === "tier") {
				if (rendered.has("tier")) return [];
				rendered.add("tier");
			}
			// Special handling for audienceAgeDistribution: aggregate percentages by ageRange
			if (
				key === "audienceAgeDistribution" &&
				Array.isArray(value) &&
				value.length > 0 &&
				value.every((v) => v && typeof v === "object" && "ageRange" in v && "percentage" in v)
			) {
				// Aggregate percentages by ageRange
				const aggregated = value.reduce((acc: { [ageRange: string]: number }, item: any) => {
					const ageRange = item.ageRange;
					const percentage = Number(item.percentage) || 0;
					if (acc[ageRange]) {
						acc[ageRange] += percentage;
					} else {
						acc[ageRange] = percentage;
					}
					return acc;
				}, {});

				// Convert aggregated object to array and sort by ageRange
				const aggregatedArray = Object.entries(aggregated)
					.map(([ageRange, percentage]) => ({ ageRange, percentage }))
					.sort((a, b) => {
						// Sort by the first number in the age range (e.g., "13-17" -> 13)
						const aStart = parseInt(a.ageRange.split("-")[0]);
						const bStart = parseInt(b.ageRange.split("-")[0]);
						return aStart - bStart;
					});

				return aggregatedArray.map((item: any, index: number) => (
					<ContentRow key={`${key}-${item.ageRange}-${index}`}>
						<ContentLabel>{`Audience Age ${item.ageRange} %`}</ContentLabel>
						<ContentSeparator>:</ContentSeparator>
						<ContentValue>{formatPercentageValue(Number(item.percentage))}</ContentValue>
					</ContentRow>
				));
			}

			// Special handling for sampleContent: display platform names as clickable links
			if (key === "sampleContent" || key === "sampleContents") {
				const urls = Array.isArray(value)
					? value.filter((v: any) => typeof v === "string" && looksLikeUrl(v))
					: typeof value === "string" && looksLikeUrl(value)
						? [value]
						: [];

				if (urls.length > 0) {
					return (
						<ContentRow key={key}>
							<ContentLabel>{formatLabel(key)}</ContentLabel>
							<ContentSeparator>:</ContentSeparator>
							<ContentValue>
								{urls.map((url: string, index: number) => (
									<React.Fragment key={index}>
										{index > 0 && " | "}
										<a href={url} target="_blank" rel="noopener noreferrer">
											{getPlatformNameFromUrl(url)}
										</a>
									</React.Fragment>
								))}
							</ContentValue>
						</ContentRow>
					);
				}
				// If not a URL, fall through to default rendering
			}

			// Category field: render as dropdown
			if (key === "category" || key === "categories") {
				const currentVal = Array.isArray(value) ? value[0] || "" : typeof value === "string" ? value : "";
				if (!isClientOnly && canManage && onCategoryTierChange && creator) {
					return (
						<ContentRow key={key}>
							<ContentLabel>{formatLabel("category")}</ContentLabel>
							<ContentSeparator>:</ContentSeparator>
							<ContentValue>
								<StyledSelect
									value={currentVal || undefined}
									onChange={(val: string) => {
										onCategoryTierChange(creator.id, "categories", val ? [val] : []);
									}}
									size="small"
									placeholder="Select"
									style={{ width: 150 }}
									options={CREATOR_CATEGORIES.map((c) => ({ value: c, label: c }))}
									allowClear
								/>
							</ContentValue>
						</ContentRow>
					);
				}
				if (!currentVal) return [];
				return (
					<ContentRow key={key}>
						<ContentLabel>{formatLabel("category")}</ContentLabel>
						<ContentSeparator>:</ContentSeparator>
						<ContentValue>{currentVal}</ContentValue>
					</ContentRow>
				);
			}

			// Creator Tier field: render as dropdown when customizing
			if (key === "creatorTier" || key === "tier") {
				const currentVal = value || "";
				if (!isClientOnly && canManage && onCategoryTierChange && creator) {
					return (
						<ContentRow key={key}>
							<ContentLabel>Creator Tier</ContentLabel>
							<ContentSeparator>:</ContentSeparator>
							<ContentValue>
								<StyledSelect
									value={currentVal || undefined}
									onChange={(val: string) => {
										onCategoryTierChange(creator.id, "tier", val);
									}}
									size="small"
									placeholder="Select"
									style={{ width: 150 }}
									options={CREATOR_TIERS.map((t) => ({ value: t, label: t }))}
									allowClear
								/>
							</ContentValue>
						</ContentRow>
					);
				}
				if (!currentVal) return [];
				return (
					<ContentRow key={key}>
						<ContentLabel>Creator Tier</ContentLabel>
						<ContentSeparator>:</ContentSeparator>
						<ContentValue>{currentVal}</ContentValue>
					</ContentRow>
				);
			}

			// Skip rationaleForProposing — replaced by recommendation from assignment
			if (key === "rationaleForProposing") {
				return [];
			}

			return (
				<ContentRow key={key}>
					<ContentLabel>{formatLabel(key)}</ContentLabel>
					<ContentSeparator>:</ContentSeparator>
					<ContentValue>
						{Array.isArray(value) && value.length > 0 && value.every((v) => typeof v === "string") ? (
							<List
								size="small"
								dataSource={value}
								split={false}
								renderItem={(item, index) => (
									<List.Item key={index} style={{ padding: 0, border: "none" }}>
										{looksLikeUrl(item) ? (
											<a href={item} target="_blank" rel="noopener noreferrer">
												{item}
											</a>
										) : (
											item
										)}
									</List.Item>
								)}
							/>
						) : (
							formatDisplayValue(key, value)
						)}
					</ContentValue>
				</ContentRow>
			);
		});
	};

	const renderBasicTab = (creator: Creator) => {
		const entries = Object.entries(creator).filter(([key]) => {
			if (RESERVED_BASIC_FIELDS.has(key)) {
				return false;
			}
			if (isHiddenRateField(key)) {
				return false;
			}
			if (Object.prototype.hasOwnProperty.call(PLATFORM_TABS, key)) {
				return false;
			}
			return typeof creator[key] !== "object" || Array.isArray(creator[key]);
		});

		const rows = renderContentRows(entries, creator);
		const assignmentId = creator.assignment?.id || creator.assignmentId;
		const savedEmail = creator.contactEmail ?? creator.assignment?.contactEmail ?? "";
		const emailRow = (
			<ContentRow key={`${creator.id}-contact-email`}>
				<ContentLabel>Email Address</ContentLabel>
				<ContentSeparator>:</ContentSeparator>
				<ContentValue>
					{assignmentId && canManage && !isClientOnly ? (
						<>
							<ContactEmailField
								type="email"
								aria-label={`Campaign contact email for ${creator.name}`}
								$invalid={Boolean(contactEmail.errors[assignmentId])}
								disabled={contactEmail.saving[assignmentId]}
								value={contactEmail.drafts[assignmentId] ?? savedEmail}
								onChange={(event) => contactEmail.setDraft(assignmentId, event.target.value)}
								onBlur={() => void contactEmail.save(assignmentId, savedEmail)}
							/>
							{contactEmail.errors[assignmentId] && (
								<ContactEmailError>{contactEmail.errors[assignmentId]}</ContactEmailError>
							)}
						</>
					) : (
						savedEmail || "—"
					)}
				</ContentValue>
			</ContentRow>
		);
		const showEditableFinalRate = canManage && !isClientOnly;

		if (!showEditableFinalRate) {
			return (
				<>
					{emailRow}
					{rows}
				</>
			);
		}

		return (
			<>
				{emailRow}
				{rows}
				<ContentRow key={`${creator.id}-final-rate-edit`}>
					<ContentLabel>Final Rate</ContentLabel>
					<ContentSeparator>:</ContentSeparator>
					<ContentValue>
						<RateInputShell $invalid={rateInvalid[creator.id]}>
							<RateSuffix>{finalRateCurrency || "USD"}</RateSuffix>
							<RateTextInput
								inputMode="decimal"
								placeholder="0.00"
								value={
									rateDrafts[creator.id] ??
									formatRateDisplayValue(
										creator.finalRate ?? creator.assignment?.finalRate,
										finalRateCurrency,
									)
								}
								onChange={(e) => {
									const nextValue = sanitizeRateInputValue(e.target.value, finalRateCurrency);
									setRateDrafts((prev) => ({
										...prev,
										[creator.id]: formatRateDisplayValue(nextValue, finalRateCurrency),
									}));
									if (
										rateInvalid[creator.id] &&
										isRateInputValid(nextValue.trim(), finalRateCurrency)
									) {
										setRateInvalid((prev) => ({ ...prev, [creator.id]: false }));
									}
									debouncedSaveFinalRates(creator);
								}}
							/>
						</RateInputShell>
					</ContentValue>
				</ContentRow>
				<ContentRow key={`${creator.id}-final-rate-converted-edit`}>
					<ContentLabel>Converted Rate</ContentLabel>
					<ContentSeparator>:</ContentSeparator>
					<ContentValue>
						<RateInputShell $invalid={convertedRateInvalid[creator.id]}>
							<RateSuffix>{finalRateConvertedCurrency || "AUD"}</RateSuffix>
							<RateTextInput
								inputMode="decimal"
								placeholder="0.00"
								value={
									convertedRateDrafts[creator.id] ??
									formatRateDisplayValue(
										creator.finalConvertedRate ?? creator.assignment?.finalConvertedRate,
										finalRateConvertedCurrency,
									)
								}
								onChange={(e) => {
									const nextValue = sanitizeRateInputValue(
										e.target.value,
										finalRateConvertedCurrency,
									);
									setConvertedRateDrafts((prev) => ({
										...prev,
										[creator.id]: formatRateDisplayValue(nextValue, finalRateConvertedCurrency),
									}));
									if (
										convertedRateInvalid[creator.id] &&
										isRateInputValid(nextValue.trim(), finalRateConvertedCurrency)
									) {
										setConvertedRateInvalid((prev) => ({ ...prev, [creator.id]: false }));
									}
									debouncedSaveFinalRates(creator);
								}}
							/>
						</RateInputShell>
					</ContentValue>
				</ContentRow>
			</>
		);
	};

	const renderPlatformTab = (creator: Creator, platformKey: string) => {
		const platformData = creator[platformKey];
		if (!platformData || typeof platformData !== "object") {
			return renderContentRows([]);
		}

		const entries = Object.entries(platformData);

		return renderContentRows(entries);
	};

	const renderTabContent = (creator: Creator, activeTab: TabType) => {
		if (activeTab === "basic") {
			return renderBasicTab(creator);
		}

		return renderPlatformTab(creator, activeTab);
	};

	const handleGroupDragStart = (e: React.DragEvent, index: number) => {
		if (!isCustomizing) return;
		setDraggedGroupIndex(index);
		e.dataTransfer.effectAllowed = "move";
	};

	const handleGroupDragOver = (e: React.DragEvent, index: number) => {
		if (!isCustomizing || draggedGroupIndex === null || draggedGroupIndex === index) return;
		e.preventDefault();

		setDragOverGroupIndex(index);

		// Reorder immediately during drag
		const newGroups = [...groups];
		const [draggedGroup] = newGroups.splice(draggedGroupIndex, 1);
		newGroups.splice(index, 0, draggedGroup);

		onGroupReorder?.(newGroups);
		setDraggedGroupIndex(index);
	};

	const handleGroupDragLeave = () => {
		setDragOverGroupIndex(null);
	};

	const handleGroupDragEnd = () => {
		setDraggedGroupIndex(null);
		setDragOverGroupIndex(null);
	};

	return (
		<>
			<style
				dangerouslySetInnerHTML={{
					__html: `
        .approvals-tooltip-overlay .ant-tooltip-inner {
          border: none !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
          padding: 0 !important;
          background: white !important;
        }
        .approvals-tooltip-overlay .ant-tooltip-arrow::before {
          border-top-color: white !important;
          border-bottom-color: white !important;
        }
      `,
				}}
			/>
			<GroupsContainer>
				{groups.map((group, groupIndex) => {
					const isExpanded = expandedGroups[group.id] ?? true;
					const isDragging = draggedGroupIndex === groupIndex;
					const isDragOver = dragOverGroupIndex === groupIndex;
					const approvedGroupApproval = getCurrentApprovedGroupApproval(groupApprovals[group.id]);
					const creatorsLeftToAdd = group.creators.filter((creator) => !creator.addedToCampaign).length;
					const canShowGroupFinalRateSum =
						group.creators.length > 0 &&
						group.creators.every((creator) => canManage || creator.assignment?.clientVisible === true);
					const areAllGroupFinalRatesHidden =
						group.creators.length > 0 &&
						group.creators.every((creator) => !canManage && creator.assignment?.clientVisible !== true);

					return (
						<GroupCard
							key={group.id}
							$isDragging={isDragging}
							$isDragOver={isDragOver}
							draggable={isCustomizing}
							onDragStart={(e) => handleGroupDragStart(e, groupIndex)}
							onDragOver={(e) => handleGroupDragOver(e, groupIndex)}
							onDragLeave={handleGroupDragLeave}
							onDragEnd={handleGroupDragEnd}
						>
							<GroupHeader>
								<GroupHeaderTop>
									<GroupTitleRow
										$hasBadge={
											Boolean(approvedGroupApproval) &&
											!isCustomizing &&
											!isClientOnly &&
											canManage
										}
									>
										<AppTooltip title={group.name} placement="topLeft" maxWidth={360} arrow={false}>
											<GroupTitle>{group.name}</GroupTitle>
										</AppTooltip>
										{approvedGroupApproval && !isCustomizing && !isClientOnly && canManage && (
											<ClientApprovedBadge>
												{`Client approved  •  ${formatApprovalDate(approvedGroupApproval.createdAt)}`}
											</ClientApprovedBadge>
										)}
									</GroupTitleRow>
									<GroupHeaderRight>
										{!isCustomizing && !isClientOnly && canManage && (
											<>
												<Tooltip
													title={
														<ApprovalsTooltipContent
															approvals={groupApprovals[group.id]}
															loading={loadingApprovals[group.id]}
															onClose={() =>
																setTooltipOpen((prev) => ({
																	...prev,
																	[group.id]: false,
																}))
															}
														/>
													}
													placement="bottomLeft"
													autoAdjustOverflow={POPUP_KEEP_IN_VIEWPORT}
													trigger="click"
													open={tooltipOpen[group.id]}
													onOpenChange={(open) => {
														setTooltipOpen((prev) => ({ ...prev, [group.id]: open }));
														if (open) {
															loadApprovals(group.id);
														}
													}}
													overlayStyle={{ maxWidth: "min(500px, calc(100vw - 32px))" }}
													overlayInnerStyle={{
														padding: "0",
														borderRadius: "8px",
														boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
														border: "none",
														background: "white",
													}}
													overlayClassName="approvals-tooltip-overlay"
												>
													<GroupActionButton>
														<ApprovalIcon />
														<span>See Approvals</span>
													</GroupActionButton>
												</Tooltip>
												<GroupToggleWrapper>
													<GroupToggle
														role="switch"
														aria-label="Visible to Client"
														aria-checked={
															groupVisibility[group.id] ?? group.clientVisible ?? false
														}
														$active={
															groupVisibility[group.id] ?? group.clientVisible ?? false
														}
														onClick={() => {
															const currentVisibility =
																groupVisibility[group.id] ??
																group.clientVisible ??
																false;
															const newVisibility = !currentVisibility;
															setVisibilityChange({ group, visible: newVisibility });
														}}
													/>
													<GroupToggleLabel>Visible to Client</GroupToggleLabel>
												</GroupToggleWrapper>
												<GroupActionButton onClick={() => onEditGroup?.(group.id)}>
													<EditIcon name="columnSetting" alt="Edit Groups" />
													<span>Edit Groups</span>
												</GroupActionButton>
												<GroupActionButton onClick={() => setGroupToDelete(group.id)}>
													<Icon name="trash" color="#D30024" size={16} />
													<span>Delete</span>
												</GroupActionButton>
												<AddApprovedAction>
													<AppButton
														variant="primary"
														disabled={
															!approvedGroupApproval ||
															!contentExecutionBlockId ||
															creatorsLeftToAdd === 0
														}
														onClick={() => setBatchGroup(group)}
														title={
															!approvedGroupApproval
																? "Client approval is required before adding creators"
																: creatorsLeftToAdd === 0
																	? "All approved creators have already been added to the campaign"
																	: "Add creators approved by the client"
														}
													>
														<span style={{ fontSize: "14px", fontWeight: "600" }}>
															Add approved creators
															{approvedGroupApproval && creatorsLeftToAdd > 0
																? ` (${creatorsLeftToAdd})`
																: ""}
														</span>
													</AppButton>
													{!approvedGroupApproval ? (
														<DisabledReason>
															Client approval is required before adding creators
														</DisabledReason>
													) : creatorsLeftToAdd === 0 ? (
														<DisabledReason>
															All approved creators have already been added to the
															campaign
														</DisabledReason>
													) : null}
												</AddApprovedAction>
											</>
										)}
										{isCustomizing && (
											<ExpandIcon onClick={() => toggleGroup(group.id)}>
												{isExpanded ? <UpOutlined /> : <DownOutlined />}
											</ExpandIcon>
										)}
										{!isCustomizing && isClientOnly && (
											<ClientActionsContainer>
												<ClientSelect
													placeholder="Select approval status"
													style={{ width: 160 }}
													value={userGroupApprovals[group.id]?.status || undefined}
													options={[
														{
															value: "pending",
															label: (
																<span
																	style={{
																		display: "flex",
																		alignItems: "center",
																		gap: "8px",
																	}}
																>
																	<ClockCircleOutlined
																		style={{ color: "#8c8c8c", fontSize: "16px" }}
																	/>
																	Pending
																</span>
															),
														},
														{
															value: "approved",
															label: (
																<span
																	style={{
																		display: "flex",
																		alignItems: "center",
																		gap: "8px",
																	}}
																>
																	<CheckCircleOutlined
																		style={{ color: "#52c41a", fontSize: "16px" }}
																	/>
																	Approve
																</span>
															),
														},
														{
															value: "rejected",
															label: (
																<span
																	style={{
																		display: "flex",
																		alignItems: "center",
																		gap: "8px",
																	}}
																>
																	<CloseCircleOutlined
																		style={{ color: "#ff4d4f", fontSize: "16px" }}
																	/>
																	Decline
																</span>
															),
														},
													]}
													onChange={(value: unknown) => {
														if (!campaignId || !blockId) {
															message.error("Missing campaign or block information");
															return;
														}

														const statusMap: {
															[key: string]: "approved" | "rejected" | "pending";
														} = {
															approved: "approved",
															rejected: "rejected",
															pending: "pending",
														};
														const status = statusMap[value as string];
														if (!status) {
															message.error("Invalid approval status");
															return;
														}

														const previousApproval = userGroupApprovals[group.id];
														const optimisticApproval = {
															id: previousApproval?.id || "temp",
															groupId: group.id,
															userId: "",
															status,
															comment: previousApproval?.comment || "",
															createdAt: new Date().toISOString(),
															updatedAt: new Date().toISOString(),
														} as GroupApproval;
														setUserGroupApprovals((prev) => ({
															...prev,
															[group.id]: optimisticApproval,
														}));

														creatorListAPI
															.createGroupApproval(campaignId, blockId, group.id, status)
															.then((approval) => {
																setUserGroupApprovals((prev) => ({
																	...prev,
																	[group.id]: approval,
																}));
															})
															.catch((error: any) => {
																setUserGroupApprovals((prev) => ({
																	...prev,
																	[group.id]: previousApproval ?? null,
																}));
																message.error(
																	error?.response?.data?.message ||
																		"Failed to create approval",
																);
															});
													}}
												/>
												<ClientActionText
													aria-label="Leave a Comment"
													onClick={() => {
														const approval = userGroupApprovals[group.id];
														setCommentTexts((prev) => ({
															...prev,
															[group.id]: approval?.comment || "",
														}));
														setCommentModalVisible((prev) => ({
															...prev,
															[group.id]: true,
														}));
													}}
												>
													<CommentOutlined style={{ fontSize: "18px" }} />
													<PhoneFullLabel>Leave a Comment</PhoneFullLabel>
													<PhoneShortLabel aria-hidden>Comment</PhoneShortLabel>
												</ClientActionText>
												<DynamicModal
													open={commentModalVisible[group.id] || false}
													onCancel={() =>
														setCommentModalVisible((prev) => ({
															...prev,
															[group.id]: false,
														}))
													}
													title="Leave a Comment"
													confirmText="Save comment"
													cancelText="Cancel"
													width={600}
													loading={submittingComment[group.id]}
													onConfirm={async () => {
														if (!campaignId || !blockId) {
															message.error("Missing campaign or block information");
															return;
														}

														setSubmittingComment((prev) => ({ ...prev, [group.id]: true }));
														try {
															const approval =
																await creatorListAPI.updateGroupApprovalComment(
																	campaignId,
																	blockId,
																	group.id,
																	commentTexts[group.id] || "",
																);
															setUserGroupApprovals((prev) => ({
																...prev,
																[group.id]: approval,
															}));
															setCommentModalVisible((prev) => ({
																...prev,
																[group.id]: false,
															}));
															message.success("Comment saved successfully");
														} catch (error: any) {
															console.error("Error updating comment:", error);
															message.error(
																error?.response?.data?.message ||
																	"Failed to save comment",
															);
														} finally {
															setSubmittingComment((prev) => ({
																...prev,
																[group.id]: false,
															}));
														}
													}}
												>
													<CommentInputLabel>Add a comment</CommentInputLabel>
													<CommentTextArea
														placeholder="Add a comment"
														value={commentTexts[group.id] || ""}
														onChange={(e) =>
															setCommentTexts((prev) => ({
																...prev,
																[group.id]: e.target.value,
															}))
														}
														autoSize={{ minRows: 4, maxRows: 8 }}
													/>
												</DynamicModal>
											</ClientActionsContainer>
										)}
									</GroupHeaderRight>
								</GroupHeaderTop>
								<GroupStats>
									{canShowGroupFinalRateSum && (
										<StatItem>
											<StatIcon>
												<DollarOutlined />
											</StatIcon>
											<StatContent>
												<StatLabel>Sum of Final rate:</StatLabel>
												<StatValue $isRate={true}>
													{finalRateCurrency}{" "}
													{formatRateMoneyDisplayValue(
														groupSums[group.id]?.primary ?? 0,
														finalRateCurrency,
													)}{" "}
													{" / "} {finalRateConvertedCurrency}{" "}
													{formatRateMoneyDisplayValue(
														groupSums[group.id]?.converted ?? 0,
														finalRateConvertedCurrency,
													)}
												</StatValue>
											</StatContent>
										</StatItem>
									)}
									<StatItem>
										<StatIcon>
											<UserOutlined />
										</StatIcon>
										<StatContent>
											<StatLabel>Total Creators:</StatLabel>
											<StatValue>{group.totalCreators ?? group.creators.length}</StatValue>
										</StatContent>
									</StatItem>
									{/* Temporarily commented out - Total Followers display
                  <StatItem>
                    <StatIcon>
                      <UsergroupAddOutlined />
                    </StatIcon>
                    <StatContent>
                      <StatLabel>Total Followers:</StatLabel>
                    <StatValue>{group.totalFollowers || '0'}</StatValue>
                    </StatContent>
                  </StatItem>
                */}
								</GroupStats>
							</GroupHeader>

							{isExpanded && (
								<CreatorsGrid>
									{group.creators.map((creator) => {
										const isVisible = creatorVisibility[creator.id] ?? false;
										// prefer local toggles over parent visibility so UI updates immediately
										const effectiveVisible = isClientOnly
											? true
											: (localCreatorVisibility[creator.id] ?? isVisible);
										const showFinalRate = canManage || creator.assignment?.clientVisible === true;
										const activeTab = activeTabs[creator.id] || "basic";
										const finalRateStr = showFinalRate
											? rateDrafts[creator.id] !== undefined
												? rateDrafts[creator.id]
												: formatFinalRateSummaryValue(
														creator.finalRate ?? creator.assignment?.finalRate,
														finalRateCurrency,
													)
											: null;
										const convertedRateStr = showFinalRate
											? convertedRateDrafts[creator.id] !== undefined
												? convertedRateDrafts[creator.id]
												: formatFinalRateSummaryValue(
														creator.finalConvertedRate ??
															creator.assignment?.finalConvertedRate,
														finalRateConvertedCurrency,
													)
											: null;
										const hasAnyRate = !!finalRateStr || !!convertedRateStr;
										const isClientRateHidden =
											isClientOnly && !showFinalRate && areAllGroupFinalRatesHidden;
										const nameRowMargin = !hasAnyRate
											? isClientRateHidden
												? "4px 0"
												: "10.5px 0"
											: "0";
										const formatRateDisplay = (value: string | null, currency: string) =>
											value
												? `${currency} ${formatRateMoneyDisplayValue(value, currency)}`
												: `${currency} 0`;

										const rateDisplay = hasAnyRate ? (
											<>
												{formatRateDisplay(finalRateStr, finalRateCurrency)}
												{finalRateStr || convertedRateStr ? " / " : ""}
												{formatRateDisplay(convertedRateStr, finalRateConvertedCurrency)}
											</>
										) : null;

										return (
											<CreatorCard key={creator.id} $isVisible={effectiveVisible}>
												<CardHeader $isVisible={effectiveVisible}>
													<ProfileImage
														displaySize={48}
														src={creator.img_url || "/default_avatar.png"}
														alt={creator.name}
														onError={(e) => {
															const t = e.currentTarget;
															if (t.src.endsWith("/default_avatar.png")) return;
															t.src = "/default_avatar.png";
														}}
													/>
													<CreatorInfo>
														<NameRow style={{ margin: nameRowMargin }}>
															<CreatorName>{creator.name}</CreatorName>
														</NameRow>

														{rateDisplay && showFinalRate && (
															<RateInfo $visible={effectiveVisible}>
																<RateIcon />
																<RateLabel>Final rate:</RateLabel>
																<RateValue $visible={effectiveVisible}>
																	{rateDisplay}
																</RateValue>
															</RateInfo>
														)}

														{canManage && (
															<GroupToggleWrapper
																style={{ opacity: rateDisplay && canManage ? 1 : 0 }}
															>
																<SmallGroupToggle
																	role="switch"
																	aria-label="Visible to client (Final rate)"
																	aria-checked={
																		localCreatorVisibility[creator.id] ??
																		creatorVisibility[creator.id] ??
																		creator.assignment?.clientVisible ??
																		false
																	}
																	$active={
																		localCreatorVisibility[creator.id] ??
																		creatorVisibility[creator.id] ??
																		creator.assignment?.clientVisible ??
																		false
																	}
																	style={{
																		opacity: isSaving ? 0.5 : 1,
																		pointerEvents:
																			isSaving || !canManage || !rateDisplay
																				? "none"
																				: "auto",
																	}}
																	onClick={(e) => {
																		if (isSaving || !canManage || !rateDisplay)
																			return;
																		e.preventDefault();
																		const currently =
																			localCreatorVisibility[creator.id] ??
																			creatorVisibility[creator.id] ??
																			creator.assignment?.clientVisible ??
																			false;
																		const next = !currently;
																		setLocalCreatorVisibility((prev) => ({
																			...prev,
																			[creator.id]: next,
																		}));
																		setGroupVisibility((prev) => ({
																			...prev,
																			[group.id]: next,
																		}));
																		_onCreatorVisibilityToggle?.(creator.id);
																		// Slightly scroll up so the "Save Changes" button is visible
																		setTimeout(() => {
																			try {
																				const buttons = Array.from(
																					document.querySelectorAll("button"),
																				);
																				const saveBtn = buttons.find((b) =>
																					(b.textContent || "")
																						.trim()
																						.toLowerCase()
																						.includes("save changes"),
																				);
																				if (saveBtn) {
																					saveBtn.scrollIntoView({
																						behavior: "smooth",
																						block: "nearest",
																					});
																				} else {
																					window.scrollBy({
																						top: -120,
																						behavior: "smooth",
																					});
																				}
																			} catch (err) {
																				// ignore
																			}
																		}, 80);
																	}}
																/>
																<CreatorToggleLabel>
																	Visible to client (Final rate)
																</CreatorToggleLabel>
															</GroupToggleWrapper>
														)}

														{isClientOnly &&
															creator.socialAccounts &&
															Object.keys(creator.socialAccounts).length > 0 && (
																<CreatorHeaderBottom $compact={isClientRateHidden}>
																	<ChannelsSection>
																		<ChannelsLabel>
																			Creator's Channels
																		</ChannelsLabel>
																		<ChannelsIcons>
																			{[
																				"tiktok",
																				"instagram",
																				"facebook",
																				"youtube",
																			].map((platform) => {
																				const account =
																					creator.socialAccounts?.[platform];
																				if (!account) return null;

																				return (
																					<ChannelIcon
																						key={platform}
																						$platform={platform}
																						href={account.url || "#"}
																						target="_blank"
																						rel="noopener noreferrer"
																						onClick={(
																							e: React.MouseEvent<HTMLAnchorElement>,
																						) => {
																							if (!account.url) {
																								e.preventDefault();
																							}
																						}}
																					>
																						<ChannelIconImage
																							name={platform as IconName}
																							alt={platform}
																							preserveOriginalColor
																						/>
																					</ChannelIcon>
																				);
																			})}
																		</ChannelsIcons>
																	</ChannelsSection>
																</CreatorHeaderBottom>
															)}
													</CreatorInfo>
													{!isClientOnly && canManage && onDeleteCreator && (
														<DeleteIcon
															onClick={() => onDeleteCreator?.(group.id, creator.id)}
														>
															<DeleteOutlined style={{ fontSize: 18 }} />
														</DeleteIcon>
													)}
												</CardHeader>

												<InterestAndRateContainer>
													<InterestButton
														$status={normalizeCreatorInterestStatus(
															creator.creatorInterested,
														)}
													>
														{(() => {
															const status = normalizeCreatorInterestStatus(
																creator.creatorInterested,
															);
															if (!status) return "—";

															if (status === "outreached") {
																return (
																	<>
																		<SendOutlined />
																		<span>
																			{getCreatorInterestStatusLabel(status)}
																		</span>
																	</>
																);
															}

															if (status === "negotiating") {
																return (
																	<>
																		<ClockCircleOutlined />
																		<span>
																			{getCreatorInterestStatusLabel(status)}
																		</span>
																	</>
																);
															}

															if (status === "interested") {
																return (
																	<>
																		<HeartIcon />
																		<span>
																			{getCreatorInterestStatusLabel(status)}
																		</span>
																	</>
																);
															}

															return (
																<>
																	<NotInterestedIcon />
																	<span>{getCreatorInterestStatusLabel(status)}</span>
																</>
															);
														})()}
													</InterestButton>
												</InterestAndRateContainer>

												<TabsContainer $isVisible={effectiveVisible}>
													<Tab
														$active={activeTab === "basic"}
														$isVisible={effectiveVisible}
														onClick={() => handleTabChange(creator.id, "basic")}
													>
														<TabIcon
															name="profile"
															alt="Basic Info"
															$active={activeTab === "basic"}
														/>
														Basic Info
													</Tab>
													<Tab
														$active={activeTab === "tiktok"}
														$isVisible={effectiveVisible}
														onClick={() => handleTabChange(creator.id, "tiktok")}
													>
														<TabIcon
															name="tiktok"
															alt="TikTok"
															preserveOriginalColor
															$active={activeTab === "tiktok"}
														/>
														TikTok
													</Tab>
													<Tab
														$active={activeTab === "instagram"}
														$isVisible={effectiveVisible}
														onClick={() => handleTabChange(creator.id, "instagram")}
													>
														<TabIcon
															name="instagram"
															alt="Instagram"
															preserveOriginalColor
															$active={activeTab === "instagram"}
														/>
														Instagram
													</Tab>
													<Tab
														$active={activeTab === "facebook"}
														$isVisible={effectiveVisible}
														onClick={() => handleTabChange(creator.id, "facebook")}
													>
														<TabIcon
															name="facebook"
															alt="Facebook"
															preserveOriginalColor
															$active={activeTab === "facebook"}
														/>
														Facebook
													</Tab>
													<Tab
														$active={activeTab === "youtube"}
														$isVisible={effectiveVisible}
														onClick={() => handleTabChange(creator.id, "youtube")}
													>
														<TabIcon
															name="youtube"
															alt="Youtube"
															preserveOriginalColor
															$active={activeTab === "youtube"}
														/>
														Youtube
													</Tab>
												</TabsContainer>

												<CardContent $isVisible={effectiveVisible}>
													{renderTabContent(creator, activeTab)}
												</CardContent>

												{!isClientOnly && canManage && (
													<CardFooter $isVisible={effectiveVisible}>
														{creator.addedToCampaign ? (
															<AddedToCampaignContainer>
																<AddedToCampaignText>
																	<AddedCheckIcon />
																	<span>Added to Campaign</span>
																</AddedToCampaignText>
																<RemoveButton
																	type="button"
																	onClick={(e) => {
																		e.preventDefault();
																		onRemoveFromCampaign?.(
																			creator.assignmentId || creator.id,
																			group.id,
																		);
																	}}
																>
																	<RemoveIcon />
																	<span>Remove</span>
																</RemoveButton>
															</AddedToCampaignContainer>
														) : (
															<AddToCampaignButton
																type="button"
																onClick={(e) => {
																	e.preventDefault();
																	onAddToCampaign?.(
																		creator.assignmentId || creator.id,
																	);
																}}
															>
																<span>+</span>
																<span>Add to Campaign</span>
															</AddToCampaignButton>
														)}
													</CardFooter>
												)}
											</CreatorCard>
										);
									})}
									<DynamicModal
										open={groupToDelete === group.id}
										onCancel={() => setGroupToDelete(null)}
										variant="danger"
										title={`Remove ${group.name || "this group"}?`}
										icon={{ src: "/icon/trash.svg", color: "#D30024", size: 24 }}
										description={
											<>
												<p>Deleting will remove this grouping and its contents.</p>
												<p>This action can’t be undone.</p>
												<p> Are you sure you want to continue?</p>
											</>
										}
										confirmText="Delete"
										onConfirm={handleDeleteGroupConfirm}
										loading={deleting}
										cancelText="Cancel"
									/>
								</CreatorsGrid>
							)}
						</GroupCard>
					);
				})}
			</GroupsContainer>
			<ApprovedGroupBatchModal
				open={Boolean(batchGroup)}
				group={batchGroup}
				approval={batchGroup ? getCurrentApprovedGroupApproval(groupApprovals[batchGroup.id]) : null}
				currency={finalRateCurrency}
				campaignName={campaignName}
				submitting={batchSubmitting}
				onCancel={() => !batchSubmitting && setBatchGroup(null)}
				onSubmit={handleBatchSubmit}
			/>
			<DynamicModal
				open={Boolean(visibilityChange)}
				onCancel={() => !visibilitySubmitting && setVisibilityChange(null)}
				title="Confirm Visibility Change"
				description={
					visibilityChange
						? `Are you sure you want to ${visibilityChange.visible ? "make visible to client" : "hide from client"} for group "${visibilityChange.group.name}"?`
						: undefined
				}
				confirmText="Yes"
				cancelText="No"
				loading={visibilitySubmitting}
				onConfirm={() => void confirmVisibilityChange()}
			/>
		</>
	);
};

// Styled components for Approvals Tooltip
const TooltipHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 24px 24px 16px 24px;
	margin-bottom: 0;
	border-bottom: 1px solid #f0f0f0;
`;

const TooltipTitle = styled.h3`
	margin: 0;
	font-size: 18px;
	font-weight: 600;
	color: #333;
`;

const CloseButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	border: none;
	background: transparent;
	cursor: pointer;
	padding: 0;
	color: #666;
	transition: color 0.2s;

	&:hover {
		color: #333;
	}
`;

const TooltipContent = styled.div`
	max-width: 500px;
	max-height: 600px;
	overflow-y: auto;
	padding: 0;
	background: white;
	border-radius: 8px;
`;

const TooltipBody = styled.div`
	padding: 24px;
`;

const ApprovalItem = styled.div`
	display: flex;
	gap: 16px;
	margin-bottom: 24px;
	padding-bottom: 24px;
	border-bottom: 1px solid #f0f0f0;

	&:last-child {
		margin-bottom: 0;
		padding-bottom: 0;
		border-bottom: none;
	}
`;

const ApprovalIconContainer = styled.div<{ $status: string }>`
	width: 32px;
	height: 32px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	background: ${(props) => (props.$status === "approved" ? "#52c41a" : "#ff4d4f")};
`;

const ApprovalContent = styled.div`
	flex: 1;
	min-width: 0;
`;

const ApprovalHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 8px;
	flex-wrap: wrap;
`;

const ApprovalStatusText = styled.span`
	font-size: 14px;
	font-weight: 500;
	color: #333;
`;

const ApprovalAuthor = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const AuthorAvatar = styled(ResponsiveAvatar)`
	width: 24px;
	height: 24px;
	border-radius: 50%;
	object-fit: cover;
`;

const AuthorName = styled.span`
	font-size: 14px;
	font-weight: 500;
	color: #333;
`;

const ApprovalTimestamp = styled.div`
	font-size: 12px;
	color: #999;
	margin-bottom: 8px;
`;

const ApprovalComment = styled.div`
	font-size: 14px;
	color: #666;
	line-height: 1.5;
	white-space: pre-wrap;
`;

interface ApprovalsTooltipContentProps {
	approvals?: GroupApproval[];
	loading?: boolean;
	onClose?: () => void;
}

export const ApprovalsTooltipContent: React.FC<ApprovalsTooltipContentProps> = ({
	approvals = [],
	loading = false,
	onClose,
}) => {
	const formatTimestamp = (timestamp: string) => {
		const date = new Date(timestamp);
		const hours = date.getHours();
		const minutes = date.getMinutes();
		const ampm = hours >= 12 ? "PM" : "AM";
		const displayHours = hours % 12 || 12;
		const displayMinutes = minutes.toString().padStart(2, "0");
		const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
		const month = monthNames[date.getMonth()];
		const day = date.getDate();

		return `${displayHours}:${displayMinutes}${ampm} - ${month} ${day}`;
	};

	const approvedItems = approvals.filter((a) => a.status.toLowerCase() === "approved");
	// A client's Decline is stored as "rejected" (see statusMap above and
	// createGroupApproval in api/creatorList.ts). "declined" is the engagement-terms
	// vocabulary and never appears on an approval row.
	const declinedItems = approvals.filter((a) => a.status.toLowerCase() === "rejected");

	if (loading) {
		return (
			<TooltipContent>
				<TooltipHeader>
					<TooltipTitle>Approvals Details</TooltipTitle>
					{onClose && (
						<CloseButton onClick={onClose}>
							<CloseOutlined style={{ fontSize: 18 }} />
						</CloseButton>
					)}
				</TooltipHeader>
				<TooltipBody>
					<div style={{ padding: "40px", textAlign: "center" }}>Loading...</div>
				</TooltipBody>
			</TooltipContent>
		);
	}

	if (approvals.length === 0) {
		return (
			<TooltipContent>
				<TooltipHeader>
					<TooltipTitle>Approvals Details</TooltipTitle>
					{onClose && (
						<CloseButton onClick={onClose}>
							<CloseOutlined style={{ fontSize: 18 }} />
						</CloseButton>
					)}
				</TooltipHeader>
				<TooltipBody>
					<div style={{ padding: "40px", textAlign: "center", color: "#999" }}>No approvals found</div>
				</TooltipBody>
			</TooltipContent>
		);
	}

	return (
		<TooltipContent>
			<TooltipHeader>
				<TooltipTitle>Approvals Details</TooltipTitle>
				{onClose && (
					<CloseButton onClick={onClose}>
						<CloseOutlined style={{ fontSize: 18 }} />
					</CloseButton>
				)}
			</TooltipHeader>
			<TooltipBody>
				{approvedItems.length > 0 && (
					<>
						{approvedItems.map((approval) => (
							<ApprovalItem key={approval.id}>
								<ApprovalIconContainer $status="approved">
									<CheckCircleOutlined style={{ color: "white", fontSize: 18 }} />
								</ApprovalIconContainer>
								<ApprovalContent>
									<ApprovalHeader>
										<ApprovalStatusText>Approved by</ApprovalStatusText>
										<ApprovalAuthor>
											{approval.authorImageUrl && (
												<AuthorAvatar
													displaySize={24}
													src={approval.authorImageUrl}
													alt={approval.authorName || "User"}
												/>
											)}
											<AuthorName>{approval.authorName || "Unknown User"}</AuthorName>
										</ApprovalAuthor>
									</ApprovalHeader>
									<ApprovalTimestamp>{formatTimestamp(approval.createdAt)}</ApprovalTimestamp>
									<ApprovalComment>{approval.comment}</ApprovalComment>
								</ApprovalContent>
							</ApprovalItem>
						))}
					</>
				)}

				{declinedItems.length > 0 && (
					<>
						{declinedItems.map((approval) => (
							<ApprovalItem key={approval.id}>
								<ApprovalIconContainer $status="declined">
									<CloseOutlined style={{ color: "white", fontSize: 18 }} />
								</ApprovalIconContainer>
								<ApprovalContent>
									<ApprovalHeader>
										<ApprovalStatusText>Declined by</ApprovalStatusText>
										<ApprovalAuthor>
											{approval.authorImageUrl && (
												<AuthorAvatar
													displaySize={24}
													src={approval.authorImageUrl}
													alt={approval.authorName || "User"}
												/>
											)}
											<AuthorName>{approval.authorName || "Unknown User"}</AuthorName>
										</ApprovalAuthor>
									</ApprovalHeader>
									<ApprovalTimestamp>{formatTimestamp(approval.createdAt)}</ApprovalTimestamp>
									<ApprovalComment>{approval.comment}</ApprovalComment>
								</ApprovalContent>
							</ApprovalItem>
						))}
					</>
				)}
			</TooltipBody>
		</TooltipContent>
	);
};
